import { BrowserModule, Title } from '@angular/platform-browser';
import {
    NgModule,
    ApplicationRef,
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA
} from '@angular/core';
import {
    removeNgStyles,
    createNewHosts,
    createInputTransfer
} from '@angularclass/hmr';
import { Idle, IdleExpiry, SimpleExpiry } from '@ng-idle/core';

/*
 * Platform and Environment providers/directives/pipes
 */
import { ENV_PROVIDERS } from './environment';

/* App Root */
import { AppComponent } from './app.component';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { AppState, InternalStateType } from './app.service';

/* Feature Modules */
import { ApiModule } from './api/api.module';
import { CoreModule }       from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { FormsModule } from '@angular/forms';

/* Routing Module */
import { AppRoutingModule } from './app-routing.module';

import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';

import { CanDeactivateGuard } from './shared/can-deactivate-route-guard/can-deactivate-guard.service';

import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';

import { HttpClientModule } from '@angular/common/http';

// Configured HTTP interceptors from core module
import { interceptors } from './core';

// Application wide providers
const APP_PROVIDERS = [
    ...APP_RESOLVER_PROVIDERS,
    AppState
];

type StoreType = {
    state?: InternalStateType,
    restoreInputValues: () => void,
    disposeOldHosts: () => void
};

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
    bootstrap: [AppComponent],
    declarations: [ AppComponent ],
    imports: [
        BrowserModule,
        ApiModule,
        CoreModule,
        HttpClientModule,
        FormsModule,
        SharedModule,
        CommonModule,
        BrowserAnimationsModule,
        NgxGoogleAnalyticsModule.forRoot('UA-106815080-1'),
        NgxGoogleAnalyticsRouterModule,
        ToastrModule.forRoot({
             positionClass: 'toast-top-center',
             timeOut: 10000,
             preventDuplicates: true,
        }),
        AppRoutingModule
    ],
    providers: [ // expose our Services and Providers into Angular's dependency injection
        ...ENV_PROVIDERS,
        ...APP_PROVIDERS,
        Title,
        Idle, SimpleExpiry, { provide: IdleExpiry, useExisting: SimpleExpiry },
        CanDeactivateGuard, 
        interceptors, // Configured HttpInterceptor services from core module
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA
      ]
})
export class AppModule {

    constructor(public appRef: ApplicationRef,
                public appState: AppState) {
        console.log('AppModule loaded');
    }

    public hmrOnInit(store: StoreType) {
        if (!store || !store.state) {
            return;
        }
        console.log('HMR store', JSON.stringify(store, null, 2));
        // set state
        this.appState._state = store.state;
        // set input values
        if ('restoreInputValues' in store) {
            let restoreInputValues = store.restoreInputValues;
            setTimeout(restoreInputValues);
        }

        this.appRef.tick();
        delete store.state;
        //delete store.restoreInputValues;
    }

    public hmrOnDestroy(store: StoreType) {
        const cmpLocation = this.appRef.components.map((cmp) => cmp.location.nativeElement);
        // save state
        const state = this.appState._state;
        store.state = state;
        // recreate root elements
        store.disposeOldHosts = createNewHosts(cmpLocation);
        // save input values
        store.restoreInputValues = createInputTransfer();
        // remove styles
        removeNgStyles();
    }

    public hmrAfterDestroy(store: StoreType) {
        // display new elements
        //todo
        store.disposeOldHosts();
        //delete store.disposeOldHosts;
    }

}
